import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/browser';

/**
 * Validate if a URL has an allowed EnergySage domain. Relative paths (e.g. "/market/")
 * are resolved against `defaultRedirectUrl` so links from emails and other EnergySage
 * frontends that pass `next=/some/path` are accepted.
 * @param {string} url - The URL to validate
 * @param {string} deployEnv - The deployment environment (prod, int, local, etc.)
 * @param {string} [defaultRedirectUrl] - Used as the base origin for resolving relative paths
 * @returns {boolean} - Whether the domain is valid for the environment
 */
export function isValidEnergySageDomain(url, deployEnv, defaultRedirectUrl) {
    if (deployEnv === 'local') {
        return true;
    }
    try {
        const base = defaultRedirectUrl ? new URL(defaultRedirectUrl).origin : undefined;
        const urlObj = new URL(url, base);
        const { hostname, protocol } = urlObj;

        // Reject non-http(s) schemes (javascript:, data:, vbscript:, etc.) even when
        // the hostname parses as an EnergySage domain — otherwise a URI like
        // `javascript://www.energysage.dev/%0aalert(1)//` slips through and executes.
        if (protocol !== 'https:' && protocol !== 'http:') {
            return false;
        }

        if (deployEnv === 'prod') {
            return hostname.endsWith('.energysage.com');
        }
        return hostname.endsWith('.energysage.dev');
    } catch (error) {
        return false;
    }
}

/**
 * Resolve a possibly-relative redirect URL against `defaultRedirectUrl` to an absolute
 * string. Returns the input unchanged if it cannot be parsed.
 */
function resolveAgainstDefault(url, defaultRedirectUrl) {
    try {
        const base = defaultRedirectUrl ? new URL(defaultRedirectUrl).origin : undefined;
        return new URL(url, base).toString();
    } catch (error) {
        return url;
    }
}

export const state = () => ({
    /**
     * External URL to redirect to after user action (e.g. login), set by next-url middleware.
     * For example: 'https://www.energysage.com/market/'.
     */
    url: '',

    /**
     * Internal route name to redirect to after user action (e.g. login).
     * For example: 'email-confirm'. This requires the user to log in first, but we don't want to clobber state.url.
     *
     * 1. https://accounts.energysage.com/email/confirm?next=https://www.energysage.com/market/ (logged out)
     * 2. https://accounts.energysage.com/login (submit credentials)
     * 3. https://accounts.energysage.com/login/success (logged in)
     * 4. https://accounts.energysage.com/email/confirm (logged in, submit confirmation code)
     * 5. https://accounts.energysage.com/login/success (logged in)
     * 6. https://www.energysage.com/market/ (logged in)
     */
    route: '',
});

export const mutations = {
    setUrl(state, url) {
        state.url = url;
    },
    removeUrl(state) {
        state.url = '';
    },
    setRoute(state, name) {
        state.route = name;
    },
    removeRoute(state) {
        state.route = '';
    },
};

export const actions = {
    /**
     * Validate and set URL, falling back to default if invalid
     * @param {object} context - Vuex action context
     * @param {object} payload
     * @param {string} payload.url - URL to validate and set
     */
    validateAndSetUrl({ commit }, { url }) {
        const deployEnv = this.$config.DEPLOY_ENV;
        const defaultUrl = this.$config.DEFAULT_REDIRECT_URL;

        if (url && isValidEnergySageDomain(url, deployEnv, defaultUrl)) {
            commit('setUrl', resolveAgainstDefault(url, defaultUrl));
        } else {
            datadogRum.addError(new Error('Invalid redirect URL rejected'), {
                url,
            });
            Sentry.captureException(new Error('Invalid redirect URL rejected'), {
                extra: {
                    url,
                },
            });

            commit('setUrl', defaultUrl);
        }
    },
};

export const getters = {};
